import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { List, Sun, Moon, User, Gear, SignOut, CaretDown } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../utils/context/AuthContext";
import { useTheme } from "../../utils/context/ThemeContext";
import useCurrentUser from "../../utils/hooks/useCurrentUser";

export default function Navbar({ isOpen, setIsOpen, isMobile }) {
  const [userOpen, setUserOpen] = useState(false);
  const { currentUser, fetchUser } = useCurrentUser();
  const { theme, toggleTheme } = useTheme();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const isDark = theme === "dark";

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogOut = (e) => {
    e.preventDefault();
    logout();
    navigate("/login");
  };

  const initials = (currentUser?.fullName || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <nav
      className={`fixed top-0 text-start right-0 z-30 h-[4.5rem] border-b border-line bg-surface glass transition-all duration-300 ${
        isMobile ? "left-0" : isOpen ? "left-[250px]" : "left-20"
      }`}
    >
      <div className="flex h-full items-center justify-between px-4 sm:px-6">
        {/* Left */}
        <div className="flex items-center gap-3">
          {isMobile && (
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="grid h-10 w-10 place-items-center rounded-xl bg-accent text-accent-fg"
            >
              <List size={20} weight="bold" />
            </button>
          )}
          <div className="hidden sm:block">
            <p className="text-sm font-semibold text-black dark:text-white">Welcome back{currentUser?.fullName ? `, ${currentUser.fullName.split(" ")[0]}` : ""}</p>
            <p className="text-xs text-black/45 dark:text-white/45">Here's what's happening today.</p>
          </div>
        </div>

        {/* Right */}
        <div className="flex items-center gap-2">
          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="grid h-10 w-10 place-items-center rounded-xl border dark:border-white/10 border-black/10 bg-white/5 text-black/70 dark:text-white/70 transition hover:bg-white/10 hover:text-white"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={theme}
                initial={{ y: -8, opacity: 0, rotate: -30 }}
                animate={{ y: 0, opacity: 1, rotate: 0 }}
                exit={{ y: 8, opacity: 0, rotate: 30 }}
                transition={{ duration: 0.18 }}
              >
                {isDark ? <Sun size={19} weight="duotone" /> : <Moon size={19} weight="fill" />}
              </motion.span>
            </AnimatePresence>
          </button>

          {/* User */}
          <div className="relative">
            <button
              onClick={() => setUserOpen((o) => !o)}
              className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 py-1.5 pl-1.5 pr-2.5 transition hover:bg-white/10"
            >
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-accent text-xs font-semibold text-accent-fg">
                {initials}
              </span>
              <span className="hidden text-left sm:block">
                <span className="block text-xs font-semibold leading-tight text-black dark:text-white">{currentUser?.fullName || "User"}</span>
                <span className="block text-[11px] leading-tight text-black/50 dark:text-white/50">{currentUser?.role?.name || "—"}</span>
              </span>
              <CaretDown size={14} className="text-white/40" />
            </button>

            <AnimatePresence>
              {userOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setUserOpen(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.97 }}
                    transition={{ duration: 0.16 }}
                    className="absolute right-0 z-20 mt-2 w-52 overflow-hidden rounded-xl border border-line bg-surface shadow-pop"
                  >
                    <div className="border-b border-line px-4 py-3">
                      <p className="text-sm font-semibold text-ink">{currentUser?.fullName}</p>
                      <p className="text-xs text-muted">{currentUser?.email}</p>
                    </div>
                    <Link
                      to="/settings"
                      onClick={() => setUserOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-ink transition hover:bg-surface-2"
                    >
                      <User size={17} /> Your profile
                    </Link>
                    <Link
                      to="/settings"
                      onClick={() => setUserOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-ink transition hover:bg-surface-2"
                    >
                      <Gear size={17} /> Settings
                    </Link>
                    <button
                      onClick={handleLogOut}
                      className="flex w-full items-center gap-2.5 border-t border-line px-4 py-2.5 text-left text-sm text-red-500 transition hover:bg-red-500/10"
                    >
                      <SignOut size={17} /> Sign out
                    </button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </nav>
  );
}
